import React, { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api/client.js';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { useDashboardData } from '../../state/DashboardDataContext.jsx';
import { clearOnboardingSkipped } from '../../lib/onboardingSkip.js';
import { markOnboardingCompleted } from '../../lib/onboardingCompletion.js';
import { BRAND_CREATED_EVENT, normalizeQuickBrandWebsite, scanQuickBrandWebsite } from '../../lib/quickBrand.js';
import { rasterizeSvgToPng } from '../../lib/rasterize.js';
import {
  mergeQuickPreviewScan, missingQuickPreviewFields, quickPreviewFields,
  quickPreviewReady, saveQuickPreviewStart,
} from '../../lib/quickPreviewStart.js';

const LABELS = { businessName: 'Name', businessDescription: 'Description', targetAudience: 'Audience', offer: 'Offer' };

/** Minimal context after Skip; the existing activation chain prepares the pack. */
export default function QuickPreviewStart({ brandProfile = null, onComplete }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth() || {};
  const dashboard = useDashboardData();
  const id = useId();
  const [fields, setFields] = useState(() => quickPreviewFields(brandProfile));
  const [website, setWebsite] = useState(brandProfile?.websiteUrl || '');
  const [step, setStep] = useState(brandProfile ? 'review' : 'website');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const busyRef = useRef(false);
  const [base] = useState(brandProfile);
  const visibleFields = base ? missingQuickPreviewFields(base) : Object.keys(LABELS);
  const valid = quickPreviewReady({ ...quickPreviewFields(base), ...fields });

  const scan = async (event) => {
    event?.preventDefault();
    if (busyRef.current || !website.trim()) return;
    busyRef.current = true; setBusy(true); setError(null);
    let validWebsite = false;
    try {
      normalizeQuickBrandWebsite(website);
      validWebsite = true;
      const result = await scanQuickBrandWebsite({ apiClient: api, businessName: fields.businessName, website, rasterizeLogo: rasterizeSvgToPng });
      setFields(current => mergeQuickPreviewScan(current, result.business));
      if (missingQuickPreviewFields(result.business).some(key => key !== 'businessName')) setError('quickStartScanFailed');
    } catch {
      // An invalid URL must not trap the manual recovery form on every save.
      if (!validWebsite) setWebsite('');
      setError('quickStartScanFailed');
    } finally {
      setStep('review'); busyRef.current = false; setBusy(false);
    }
  };

  const save = async (event) => {
    event.preventDefault();
    if (busyRef.current || step === 'saved' || error === 'quickStartChanged') return;
    if (!valid) { setError('quickStartRequired'); return; }
    busyRef.current = true; setBusy(true); setError(null);
    try {
      const result = await saveQuickPreviewStart({
        apiClient: api, brandProfile: base, fields, website,
        language: base?.language || i18n.resolvedLanguage || i18n.language || 'en',
      });
      api.invalidateCache?.(['/api/brand-profile', '/api/profile/brands', '/api/onboarding/status', '/api/strategy']);
      clearOnboardingSkipped(user?.id);
      markOnboardingCompleted();
      await Promise.allSettled([
        dashboard.invalidateResource?.('setupStatus'), dashboard.invalidateResource?.('socialAccounts'),
      ]);
      window.dispatchEvent(new CustomEvent(BRAND_CREATED_EVENT, { detail: { name: fields.businessName.trim() } }));
      setStep('saved');
      onComplete?.(result);
    } catch (err) {
      setError(err?.code === 'PREVIEW_BRAND_CHANGED' || err?.status === 409 ? 'quickStartChanged' : 'quickStartSaveFailed');
    } finally {
      busyRef.current = false; setBusy(false);
    }
  };

  if (step === 'saved') return <p role="status">{t('freePreview.quickStartSaved')}</p>;
  return (
    <div className="fpb-quick-start" aria-busy={busy}>
      {step === 'website' ? <form onSubmit={scan}>
        <label className="fpb-quick-field" htmlFor={`${id}-website`}>
          <span>{t('freePreview.quickStartWebsiteLabel')}</span>
          <input id={`${id}-website`} type="text" inputMode="url" autoComplete="url" maxLength={2000}
            value={website} placeholder={t('freePreview.quickStartWebsitePlaceholder')} disabled={busy}
            onChange={event => setWebsite(event.target.value)} />
        </label>
        <div className="fpb-next-row">
          <button type="submit" className="btn btn-primary" disabled={busy || !website.trim()}>{t(`freePreview.${busy ? 'quickStartScanning' : 'quickStartScan'}`)}</button>
          <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => { setWebsite(''); setError(null); setStep('review'); }}>{t('freePreview.quickStartManual')}</button>
        </div>
      </form> : <form onSubmit={save}>
        <p className="fpb-muted">{t('freePreview.quickStartReviewHelp')}</p>
        <div className="fpb-quick-fields">
          {visibleFields.map(key => <label className="fpb-quick-field" key={key} htmlFor={`${id}-${key}`}>
            <span>{t(`freePreview.quickStart${LABELS[key]}Label`)}</span>
            {key === 'businessDescription'
              ? <textarea id={`${id}-${key}`} rows={2} maxLength={1000} value={fields[key]} disabled={busy} onChange={event => setFields(current => ({ ...current, [key]: event.target.value }))} />
              : <input id={`${id}-${key}`} type="text" maxLength={key === 'businessName' ? 200 : 1000} value={fields[key]} disabled={busy} onChange={event => setFields(current => ({ ...current, [key]: event.target.value }))} />}
          </label>)}
        </div>
        {error && <p role="alert">{t(`freePreview.${error}`)}</p>}
        <div className="fpb-next-row">
          <button type="submit" className="btn btn-primary" disabled={busy || !valid || error === 'quickStartChanged'}>{t(`freePreview.${busy ? 'quickStartSaving' : 'quickStartStart'}`)}</button>
          <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => { setError(null); setStep('website'); }}>{t('freePreview.quickStartUseWebsite')}</button>
        </div>
      </form>}
    </div>
  );
}
